<template>
    <v-layout align-center justify-center class="upload_bg">
        <!-- GLOBAL APP BAR HEADER -->
        <v-toolbar fixed flat class="transparent white--text">
            <span v-if="!isMobile">SweldoNow Upload Photo</span>
            <v-spacer></v-spacer>
            <!-- WORKFLOW TOGGLE KEYS -->
           
            <v-toolbar-items>    
                <v-btn dark large flat @click="turnOnVideo" class="mr-3 mt-3" v-show="showCameraButton">
                    <v-icon class="mr-10">photo_camera</v-icon>Use Camera
                </v-btn>
                <v-btn dark large flat @click="turnOffVideo" class="mr-3" v-show="showUploadButton">
                    <v-icon class="mr-10">cloud_upload</v-icon>Upload File
                </v-btn>   
                <v-btn dark flat @click="closeDialog">Close</v-btn>
            </v-toolbar-items>
        </v-toolbar>

        <v-alert v-if="error" :value="true" type="error" icon="error" dismissible class="mb-4">
            {{ errorMessage }}
        </v-alert>

        <!-- CONTAINER CONTENT BOX -->
        <v-flex xs12 sm7 :class="isMobile ? 'content-container-mobile' : ''">  
            
            <div class="text-xs-center" :class="isMobile ? 'content-container-main-mobile' : ''">
                <!-- LIVE CAMERA VIEWFINDER -->
                <div v-show="showCameraScreen" class="camera-frame mb-3">
                    <video autoplay playsinline muted class="video-image"></video>
                </div>

                <!-- CAPTURED SNAPSHOT CANVAS INTERFACE -->
                <div v-show="showImage" class="camera-frame mb-3">
                    <img id="imgMyProfile" src="" class="video-image">
                </div>

                <!-- DRAG AND DROP FILE COMPONENT LAYER -->
                <div v-show="showUpload" class="text-xs-center">
                    <vue-dropzone ref="myVueDropzone" 
                        class="dropzone-component"
                        id="dropzone" 
                        :options="dropzoneOptions"
                        v-on:vdropzone-success="uploadSuccess"
                        v-on:vdropzone-file-added="fileAdded"
                    ></vue-dropzone>
                </div>
            </div>

            <!-- DYNAMIC CONTROL ACTIONS BAR CONTAINER -->
            <div :class="isMobile ? 'img-controls' : 'text-xs-center mt-4'">
                <v-btn dark large @click="takeScreenshot" :loading="loadingScreenshot" v-show="showScreenshot" color="primary" title="Take Picture">
                    <v-icon class="mr-2">linked_camera</v-icon>Take Picture
                </v-btn>
                <v-btn dark large @click="takeAgain" v-show="showSavePic" outline class="mr-2" title="Take Another">
                    <v-icon class="mr-2">refresh</v-icon>Take Again
                </v-btn>
                <v-btn dark large @click="savePicture" :loading="uploading" v-show="showSavePic" color="primary" title="Save Picture">
                    <v-icon class="mr-2">save</v-icon>Save Photo
                </v-btn>
            </div>
        </v-flex> 

    </v-layout>
</template>

<script>
import axios from 'axios'
import vue2Dropzone from 'vue2-dropzone'
import 'vue2-dropzone/dist/vue2Dropzone.min.css'
import pageKeys from '@/common/pagekeys'

export default {
    name: 'UploadPhoto',
    components: {
        vueDropzone: vue2Dropzone
    },
    props: ['pageKey'],
    data () {
        return {
            userId: 0,
            loading: false,   
            video: null,
            videoStream: null,
            
            // View state visibility triggers
            showCameraButton: true,       
            showUploadButton: false,
            showCameraScreen: false,
            showScreenshot: false,
            showImage: false,
            showSavePic: false,
            showUpload: true,      
            loadingScreenshot: false,            
            uploading: false,
            error: false,
            errorMessage: '', 
        }
    },    
    methods: {
        // --- LIVE HARDWARE STREAM CONTROLS ---
        turnOnVideo() {
            try {
                this.error = false
                this.errorMessage = ''
                this.showCameraScreen = true
                this.showCameraButton = false
                this.showUploadButton = true
                this.showUpload = false
                this.showScreenshot = true
                this.loadingScreenshot = true
                this.showImage = false
                this.showSavePic = false

                // Mirrors your explicit video dimensions array rule check
                const constraints = {
                    video: { width: { exact: 440 }, height: { exact: 360 } }
                };
                this.video = document.querySelector('video');

                navigator.mediaDevices.getUserMedia(constraints)
                    .then((stream) => {
                        this.video.srcObject = stream
                        this.videoStream = stream
                        this.loadingScreenshot = false
                    })
                    .catch((err) => {
                        this.loadingScreenshot = false
                        this.error = true
                        this.errorMessage = "Could not open camera stream. Please check app permissions."
                        this.turnOffVideo()
                    });
            } catch(error) {
                this.error = true
                this.errorMessage = error
            }
        },
        turnOffVideo() {
            this.turnOffDevice()
            this.showCameraScreen = false
            this.showScreenshot = false
            this.showUpload = true
            this.showImage = false
            this.showSavePic = false
            this.showCameraButton = true
            this.showUploadButton = false
        },
        turnOffDevice() {
            if (this.videoStream !== null) {
                this.videoStream.getTracks().forEach((track) => {
                    track.stop()
                });
                this.videoStream = null
            }
        },
        takeAgain() {
            this.showImage = false
            this.showSavePic = false
            this.turnOffDevice()
            this.turnOnVideo()
        },

        // --- CAMERA FRAMECAP SNAPS ---
        async takeScreenshot() {
            this.showImage = true
            this.img = document.getElementById('imgMyProfile')
            
            const canvas = document.createElement('canvas');
            
            // FORCE A HIGH-RESOLUTION TARGET FRAME (e.g. 1080x1080 standard 2x2 layout)
            const TARGET_SIZE = 1080;
            canvas.width = TARGET_SIZE;
            canvas.height = TARGET_SIZE;
            
            const ctx = canvas.getContext('2d');

            // Calculate center-square coordinates from your webcam stream to prevent distortion
            const sourceWidth = this.video.videoWidth;
            const sourceHeight = this.video.videoHeight;
            const sourceSize = Math.min(sourceWidth, sourceHeight);
            
            const sx = (sourceWidth - sourceSize) / 2;
            const sy = (sourceHeight - sourceSize) / 2;

            // Draw and smoothly scale the webcam image up to 1080x1080
            ctx.drawImage(
                this.video, 
                sx, sy, sourceSize, sourceSize, // Source square dimensions (e.g., center box of 360x360)
                0, 0, TARGET_SIZE, TARGET_SIZE   // Upscaled destination square dimensions
            );
            
            // Export native image as a high-quality jpeg
            this.img.src = canvas.toDataURL('image/jpeg', 0.95);
            
            this.showCameraScreen = false
            this.showScreenshot = false
            this.showSavePic = true
            this.turnOffDevice()
        },
        async savePicture() {
            try {
                this.uploading = true;
                this.error = false;
                this.errorMessage = '';
                
                // 1. Get raw base64 data from preview image element
                const base64Image = this.img.src;
                
                // 2. Convert base64 data string into a raw Binary Blob file
                const byteString = atob(base64Image.split(',')[1]);
                const mimeString = base64Image.split(',')[0].split(':')[1].split(';')[0];
                const ab = new ArrayBuffer(byteString.length);
                const ia = new Uint8Array(ab);
                for (let i = 0; i < byteString.length; i++) {
                    ia[i] = byteString.charCodeAt(i);
                }
                const blob = new Blob([ab], { type: mimeString });
                
                // 3. Package into FormData payload to mimic dropzone's payload format
                const formData = new FormData();
                formData.append('file', blob, `camera_${Date.now()}.jpg`);

                // 4. Send HTTP POST upload request to your existing backend endpoint
                const uploadUrl = `${axios.defaults.baseURL}Image/upload/faceai-training-photo`;
                const uploadHeaders = { 
                    "Authorization": `Bearer ${localStorage.token}`,
                    "page_key": pageKeys.attendance_face_activation,
                    "session_id": localStorage.sessionId,
                    "source": "Customer",
                    "Content-Type": "multipart/form-data" // Crucial header for file multi-part uploads
                };

                const response = await axios.post(uploadUrl, formData, { headers: uploadHeaders });

                if (response && response.status === 200) {
                    // 5. Send backend's file tracking payload response upstream to your parent FaceAI processPhoto
                    this.$emit('send', response.data); 
                    this.uploading = false;
                    this.closeDialog();
                } else {
                    throw new Error("Failed to upload image from camera device.");
                }
            } catch (error) {
                this.uploading = false;
                this.error = true;
                this.errorMessage = error.message || error;
            }
        },

        // --- FILE DROPZONE ACTION INTEGRATIONS ---
        fileAdded(file) {
            this.loading = true
            this.error = false
        },
        async uploadSuccess(file, response) {
            this.$refs.myVueDropzone.removeAllFiles()
            
            // Emits standardized payload model map layout
            const dataPayload = {
                base64Image: file.dataURL || response.base64Image,
                fileName: response.fileName
            }
            
            this.$emit('send', dataPayload)
            this.closeDialog()
        },
        closeDialog() {
            this.turnOffDevice()
            this.showUpload = true
            this.showCameraScreen = false
            this.showCameraButton = true
            this.showUploadButton = false
            this.showScreenshot = false
            this.showImage = false
            this.showSavePic = false
            this.error = false
            this.errorMessage = ''
            
            this.$emit('update:dialogUploadPhoto', false)     
        }
    },        
    created() {
        this.userId = this.$store.state.userId
    },
    computed: {
        dropzoneOptions() {    
            return {   
                url: `${axios.defaults.baseURL}Image/upload/faceai-training-photo`,    
                headers: { 
                    "Authorization": `Bearer ${localStorage.token}`,
                    "page_key": pageKeys.attendance_face_activation,
                    "session_id": localStorage.sessionId,
                    "source": "Customer"
                },
                addRemoveLinks: false, 
                
                // --- CUSTOM DICTIONARY ERROR STRINGS ---
                dictInvalidFileType: "Unsupported Format. Please choose a JPG photo.",
                dictFileTooBig: "File size exceeds 5MB limit.",
                dictMaxFilesExceeded: "You can only upload one photo at a time.",
                
                dictDefaultMessage: this.dropDescription,
                maxFiles: 1,
                acceptedFiles: 'image/jpeg', 
                maxFilesize: 5.0,

                // INTERCEPT REJECTIONS AND BIND CLICK EVENTS DIRECTLY TO THE ALERT BOX
               init: function() {
                   this.on("error", function(file, message) {
                       // Target our custom styled red banner class block element
                       const errorEl = file.previewElement.querySelector('.dz-error-message');
                       if (errorEl) {
                           errorEl.addEventListener('click', () => {
                               this.removeFile(file); // Purge file instance when tapped or clicked
                           });
                       }
                   });
               }
            }
        },
        dropDescription() {
            return `<p class='subheading'><u>${this.isMobile ? 'Tap to browse gallery' : 'Drop your photo here or click to browse gallery'}</u></p><br>
                    <p class='body-1 text-xs-left'><b>IMPORTANT:</b></p>
                    <ul class='text-xs-left'>
                        <li>File Type: JPG only</li>
                        <li>Dimensions: Close-up photo showing only your face</li>
                        <li>File Size: Up to 5MB</li>
                    </ul><br>
                    <p class='text-xs-left'><b>TIP: </b>Ensure your face is clearly visible, fully lit, and centered.</p>`
        }
    },
    destroyed() {
        this.turnOffDevice()
    }
}
</script>
 
<style scoped>
.upload_bg {
    height: 100%;
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    background-color: rgba(0, 0, 0, 0.9);
}

/* RESPONSIVE LAYOUT CONTAINERS */
.content-container-mobile {
    position: relative !important; 
    height: 100% !important;
}

.content-container-main-mobile {
    position: absolute !important; 
    top: 15% !important; 
    width: 100% !important;
}

/* MEDIA CONTENT WRAPPERS */
.camera-frame {
    max-width: 440px;
    margin: 0 auto;
    overflow: hidden;
}

.video-image {
    max-width: 100% !important; 
    width: auto !important; 
    height: auto !important; 
    border-radius: 8px !important;
}

/* DROPZONE SURFACE */
.dropzone-component {
    height: 300px !important;
    padding: 10px 50px 0px 50px !important;
}

/* CONTROL LAYOUT ELEMENT PANELS */
.img-controls {
    position: absolute; 
    bottom: 5%; 
    left: 50%; 
    transform: translateX(-50%); 
    width: 90%; 
    padding: 10px 10px 20px 10px;
    box-sizing: border-box;
    
    /* CRITICAL FIXES FOR MOBILE VIEWPORTS */
    z-index: 99 !important; /* Forces buttons to float ON TOP of the dark background */
    display: flex !important;
    flex-direction: column !important; /* Stacks buttons cleanly on mobile phones */
    gap: 10px !important; /* Adds professional spacing between stacked buttons */
}

/* ==========================================================================
   DROPZONE VUE-DEEP STYLING OVERRIDES (CENTERED TEXT ONLY MINIMALIST LOOK)
   ========================================================================== */

/* 1. DROPZONE SURFACE CANVAS */
::v-deep #dropzone {
    border: 2px dashed rgba(255, 255, 255, 0.3) !important;
    border-radius: 8px;
    background-color: rgba(0, 0, 0, 0.4) !important;
    color: white !important;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    margin: 10px;
}

/* 2. THE CARD CONTAINER BOX */
::v-deep #dropzone .dz-preview {
    /* Changed from translucent grey to a solid dark grey/black to hide the white box completely */
    background: #1e1e1e !important; 
    border: 1px solid rgba(255, 255, 255, 0.15) !important;
    border-radius: 8px !important;
    padding: 20px !important;
    width: 280px !important; 
    position: absolute !important;
    left: 50% !important;
    top: 50% !important;
    transform: translate(-50%, -50%) !important;
    margin: 0 !important;
    box-sizing: border-box !important;
    box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.5) !important; /* Added shadow to make it pop against the background */
}

/* Display normally for standard/successful uploads */
::v-deep #dropzone .dz-preview .dz-image {
    border-radius: 6px !important;
    margin: 0 auto 15px auto !important;
    display: block !important;
    width: 120px !important;
    height: 120px !important;
}

/* CRITICAL: Completely hide the preview image frame ONLY when an error occurs */
::v-deep #dropzone .dz-preview.dz-error .dz-image {
    display: none !important;
}

/* 3. DETAILS LABELS LAYER CLEANUP */
::v-deep #dropzone .dz-preview .dz-details {
    color: #FFF !important;
    background: transparent !important;
    opacity: 1 !important;
    position: static !important;
    padding: 0 !important;
    text-align: center !important;
    margin-bottom: 12px !important;
}

/* 4. DYNAMIC ERROR NOTIFICATION BOX */
::v-deep #dropzone .dz-preview .dz-error-message {
    display: none !important; 
}

::v-deep #dropzone .dz-preview.dz-error .dz-error-message {
    position: static !important; 
    opacity: 1 !important;
    display: block !important;
    background: #FF5252 !important; /* Vuetify Red */
    color: white !important;
    border-radius: 4px !important;
    padding: 12px 16px !important;
    font-size: 13px !important;
    font-weight: bold !important;
    text-align: center !important;
    margin: 5px 0 0 0 !important;
    width: 100% !important;
    box-sizing: border-box !important;
    cursor: pointer !important;
    transition: background-color 0.2s ease;
}

::v-deep #dropzone .dz-preview.dz-error .dz-error-message:hover {
    background-color: #D32F2F !important; /* Darker Red state accent upon hover/tap */
}

::v-deep #dropzone .dz-preview .dz-error-message:after {
    display: none !important;
}

/* Hide native success/error status overlay checkmarks and standard remove strings */
::v-deep #dropzone .dz-preview .dz-success-mark,
::v-deep #dropzone .dz-preview .dz-error-mark,
::v-deep #dropzone .dz-preview .dz-remove {
    display: none !important;
}
</style>